import {
    Languages,
    ModelType,
    PrintableDocumentType,
    DocumentToGenerate,
    GenerateDocumentPdf,
} from '@livv/models';
import { AnalysisDisplayPublicWithPremiumStatus } from '@livv/models/firebaseFunctions';
import LoadingButton from '@mui/lab/LoadingButton';
import { CircularProgress } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import useTranslation from 'next-translate/useTranslation';
import { FC, useState } from 'react';
import PrintModal, { PersonalizedDocumentValues } from './PrintModal';
import { generateDocumentPdf } from '@functions';
import useAlertSnackBar from '@utils/hooks/useAlertSnackBar';
import useAmplitude from '@utils/hooks/useAmplitude';
import { TrackingEventName } from '@utils/types/amplitude/events';

const getLocalObjectUrl = async (url: string): Promise<string> => {
    const fileBlob = await (await fetch(url)).blob();

    return URL.createObjectURL(fileBlob);
};

/** Format title and download file using system dialog */
const createFileDownloadPrompt = (documentName: string, localObjectUrl: string) => {
    const downloadLink = document.createElement('a');
    downloadLink.href = localObjectUrl;
    downloadLink.download = `${documentName}.pdf`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
};

interface PrintButtonProps {
    analysesWithPremiumStatus?: AnalysisDisplayPublicWithPremiumStatus;
    customButtonHandler?: () => void;
    disabled?: boolean;
    documentId: string;
    documentName: string;
    documentType: PrintableDocumentType;
    plainButton?: boolean;
    linkedPublicationSheets?: string[]; // IDs of publication sheets cited in the document
}

const PrintButton: FC<PrintButtonProps> = ({
    analysesWithPremiumStatus,
    customButtonHandler,
    disabled = false,
    documentId,
    documentName,
    documentType,
    plainButton,
    linkedPublicationSheets,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { t, lang } = useTranslation('common');
    const [errorSnackBar, setErrorMessage] = useAlertSnackBar({ severity: 'error' });
    const { track } = useAmplitude();

    const fetchDocumentPdf = async (documents: DocumentToGenerate[], documentTitle: string) => {
        try {
            setIsLoading(true);

            // Amplitude event
            track({
                name: TrackingEventName.PDF_DOWNLOAD,
                params: {
                    documents,
                },
            });

            const documentsWithLanguage: GenerateDocumentPdf['input'] = {
                documents,
                language: lang as Languages,
            };
            const response = (await generateDocumentPdf(documentsWithLanguage))
                .data as unknown as GenerateDocumentPdf['output'];
            if (response.status === 'error')
                throw new Error('Printing error: cloud function responded with ERROR status');
            const localObjectUrl = await getLocalObjectUrl(response.url);
            createFileDownloadPrompt(documentTitle, localObjectUrl);
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error occurred while printing a document');
            setErrorMessage(t(`print.error`));
        } finally {
            setIsLoading(false);
        }
    };

    const handleModalSubmit = ({
        displaySameCaseDecisions,
        linkedAnalyses,
        note,
        withAssociatedDocuments,
        withFollowupQuestions,
    }: PersonalizedDocumentValues) => {
        const document: DocumentToGenerate = {
            id: documentId,
            type: documentType,
        };

        // Add Decision specific fields
        if (documentType === ModelType.DECISION) {
            document.displaySameCaseDecisions = displaySameCaseDecisions;
            document.linkedAnalyses = linkedAnalyses;
            document.note = note;
        }

        // Add AiSearch specific fields
        if (documentType === 'AiSearch') {
            document.withFollowupQuestions = withFollowupQuestions;
        }
        if (documentType === 'AiSearch' && withAssociatedDocuments && linkedPublicationSheets) {
            const publicationSheets: DocumentToGenerate[] = linkedPublicationSheets.map((id) => ({
                id,
                type: ModelType.PUBLICATION_SHEET,
            }));
            const supplementDocumentsTitle = `${documentName}-Annexes`;
            fetchDocumentPdf(publicationSheets, supplementDocumentsTitle);
        }

        fetchDocumentPdf([document], documentName);
        setIsModalOpen(false);
    };

    const handleDefaultButtonClick = async () => {
        if (ModelType.DECISION === documentType || documentType === 'AiSearch') {
            setIsModalOpen(true);
        } else {
            fetchDocumentPdf(
                [
                    {
                        id: documentId,
                        type: documentType,
                    },
                ],
                documentName,
            );
        }
    };

    const trackButtonClickEvent = () => {
        track({
            name: TrackingEventName.BUTTON_PDF_DOWNLOAD_CLICKED,
            params: {
                documents: [
                    {
                        id: documentId,
                        type: documentType,
                    },
                ],
            },
        });
    };

    const onButtonClick = (
        customClickHandler: (() => void) | undefined,
        defaultClickHandler: () => void,
    ) => {
        trackButtonClickEvent();
        (customClickHandler || defaultClickHandler)();
    };

    return (
        <>
            <LoadingButton
                disabled={disabled || isLoading}
                loading={isLoading}
                loadingIndicator={<CircularProgress size={24} sx={{ color: 'primary.main' }} />}
                onClick={() => onButtonClick(customButtonHandler, handleDefaultButtonClick)}
                variant={plainButton ? 'contained' : 'outlined'}
            >
                {t('print.print')}
            </LoadingButton>
            {errorSnackBar}
            <PrintModal
                analysesWithPremiumStatus={analysesWithPremiumStatus}
                documentType={documentType}
                handleSubmit={handleModalSubmit}
                isOpen={isModalOpen}
                setIsOpen={setIsModalOpen}
            />
        </>
    );
};

export default PrintButton;
